/**
 * SourcesLens
 *
 * Source-pivoted view of the same reconciliation dataset used by the
 * Channels lens. Each row is an individual lead source (Facebook Ads,
 * Angi Leads, Google Ads, …) showing quality, duplicates, conversion,
 * and the channel it rolls up into. Powered by the SAME engine —
 * server-side growth-channel rollups — so the numbers reconcile with the
 * Channels lens row-for-row.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Opportunity } from "@/data/opportunityTypes";
import { isNetWon } from "@/data/opportunityTypes";
import type { Job } from "@/data/types";
import type { IntakeRecord } from "@/data/leadIntegrityEngine";
import { LEAD_SOURCE_CHANNEL_MAP } from "@/data/sampleDataRegistry";
import { GROWTH_CHANNELS, getChannelById } from "@/lib/growthChannelMapping";
import { cn } from "@/lib/utils";
import { ArrowRight, Search, ShieldCheck, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";
import { useGrowthSources } from "@/hooks/useGrowthChannelsServerPaging";

interface SourceRow {
  name: string;
  channelId: string | null;
  channelName: string;
  leads: number;
  appointments: number;
  wonNet: number;
  revenue: number;
  intakeTotal: number;
  intakeApproved: number;
  duplicates: number;
  outOfArea: number;
  validPct: number;
  dupePct: number;
  netConvPct: number;
}

const fmt$ = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `$${n.toLocaleString()}`;
const fmtPct = (n: number) => `${Math.round(n)}%`;

function resolveChannelId(source: string | undefined): string | null {
  if (!source) return null;
  if (source in LEAD_SOURCE_CHANNEL_MAP) return LEAD_SOURCE_CHANNEL_MAP[source] ?? null;
  const lower = source.toLowerCase();
  if (lower.includes("facebook") || lower.includes("instagram") || lower.includes("meta")) return "meta-growth-engine";
  if (lower.includes("google")) return "google-ads";
  if (lower.includes("angi") || lower.includes("homeadvisor")) return "angi";
  if (lower.includes("thumbtack")) return "thumbtack";
  if (lower.includes("referral")) return "referral-engine";
  return null;
}

export interface SourcesLensProps {
  opportunities?: Opportunity[];
  jobs?: Job[];
  intakeRecords?: IntakeRecord[];
  /** Optional pre-selected channel filter (e.g. from a Channels deep-link). */
  defaultChannel?: string;
}

export default function SourcesLens({ opportunities, jobs, intakeRecords, defaultChannel }: SourcesLensProps) {
  const [query, setQuery] = useState("");
  const [channelFilter, setChannelFilter] = useState<string>(defaultChannel ?? "all");
  const useServerRows = !opportunities || !jobs || !intakeRecords;
  const serverSources = useGrowthSources({ enabled: useServerRows });

  // Sync filter when deep-link changes
  useEffect(() => {
    if (defaultChannel) setChannelFilter(defaultChannel);
  }, [defaultChannel]);

  const rows = useMemo<SourceRow[]>(() => {
    if (useServerRows) {
      return serverSources.data
        .map((row) => ({
          name: row.name,
          channelId: row.channelId,
          channelName: row.channelName,
          leads: row.leads,
          appointments: row.appointments,
          wonNet: row.wonNet,
          revenue: row.revenue,
          intakeTotal: row.intakeTotal,
          intakeApproved: row.intakeApproved,
          duplicates: row.duplicates,
          outOfArea: row.outOfArea,
          validPct: row.validPct,
          dupePct: row.dupePct,
          netConvPct: row.netConvPct,
        }))
        .sort((a, b) => b.leads - a.leads || b.revenue - a.revenue);
    }

    const map = new Map<string, SourceRow>();
    const ensure = (name: string): SourceRow => {
      let r = map.get(name);
      if (!r) {
        const channelId = resolveChannelId(name);
        r = {
          name,
          channelId,
          channelName: channelId ? (getChannelById(channelId)?.name ?? "Unattributed") : "Unattributed",
          leads: 0, appointments: 0, wonNet: 0, revenue: 0,
          intakeTotal: 0, intakeApproved: 0, duplicates: 0, outOfArea: 0,
          validPct: 0, dupePct: 0, netConvPct: 0,
        };
        map.set(name, r);
      }
      return r;
    };

    for (const opp of opportunities ?? []) {
      const name = (opp.leadSource || "Unattributed").trim() || "Unattributed";
      const r = ensure(name);
      r.leads += 1;
      if (opp.stage !== "Lead") r.appointments += 1;
      if (isNetWon(opp)) r.wonNet += 1;
    }

    for (const job of jobs ?? []) {
      const name = (job.marketing?.leadSource || "Unattributed").trim() || "Unattributed";
      const r = ensure(name);
      r.revenue += getJobDisplayValue(job);
    }

    for (const rec of intakeRecords ?? []) {
      const name = (rec.normalizedData?.leadSource || "Unattributed").trim() || "Unattributed";
      const r = ensure(name);
      r.intakeTotal += 1;
      if (rec.disposition === "approved" || rec.disposition === "approved_with_warning") r.intakeApproved += 1;
      if (rec.disposition === "duplicate_flagged" || (rec.duplicateMatches?.length ?? 0) > 0) r.duplicates += 1;
      if (rec.geoResult === "out_of_area") r.outOfArea += 1;
    }

    // Finalize derived %s
    for (const r of map.values()) {
      r.validPct = r.intakeTotal > 0 ? (r.intakeApproved / r.intakeTotal) * 100 : (r.leads > 0 ? 75 : 0);
      r.dupePct = r.intakeTotal > 0 ? (r.duplicates / r.intakeTotal) * 100 : 0;
      r.netConvPct = r.leads > 0 ? (r.wonNet / r.leads) * 100 : 0;
    }

    return Array.from(map.values()).sort((a, b) => b.leads - a.leads || b.revenue - a.revenue);
  }, [useServerRows, serverSources.data, opportunities, jobs, intakeRecords]);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (channelFilter !== "all" && (r.channelId ?? "unattributed") !== channelFilter) return false;
      if (query && !r.name.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [rows, query, channelFilter]);

  const totals = useMemo(() => ({
    sources: filtered.length,
    unmapped: filtered.filter(r => !r.channelId).length,
    intakeTotal: filtered.reduce((s, r) => s + r.intakeTotal, 0),
    intakeApproved: filtered.reduce((s, r) => s + r.intakeApproved, 0),
    duplicates: filtered.reduce((s, r) => s + r.duplicates, 0),
    outOfArea: filtered.reduce((s, r) => s + r.outOfArea, 0),
  }), [filtered]);

  const blendedValid = totals.intakeTotal > 0 ? Math.round((totals.intakeApproved / totals.intakeTotal) * 100) : 0;
  const dupePct = totals.intakeTotal > 0 ? Math.round((totals.duplicates / totals.intakeTotal) * 100) : 0;
  const oopPct = totals.intakeTotal > 0 ? Math.round((totals.outOfArea / totals.intakeTotal) * 100) : 0;

  return (
    <div className="space-y-6">
      {serverSources.loading && (
        <p className="text-[12px] text-muted-foreground">Loading server source rollups...</p>
      )}
      {serverSources.error && (
        <p className="text-[12px] text-warning">Source rollup view failed to load. Apply the growth-channel SQL migration and refresh.</p>
      )}

      {/* Quality strip — hygiene lens (funnel totals live in Channels tab) */}
      <dl className="grid grid-cols-2 sm:grid-cols-5 gap-x-6 gap-y-3 px-4 py-4 border-y border-border">
        <Stat label="Sources" value={String(totals.sources)} sub={totals.unmapped > 0 ? `${totals.unmapped} unmapped` : "all mapped"} subTone={totals.unmapped > 0 ? "warn" : undefined} />
        <Stat label="Intake quality" value={`${blendedValid}%`} sub={`${totals.intakeApproved}/${totals.intakeTotal} approved`} emphasis />
        <Stat label="Duplicates" value={`${dupePct}%`} sub={`${totals.duplicates} flagged`} subTone={dupePct > 15 ? "warn" : undefined} />
        <Stat label="Out-of-area" value={`${oopPct}%`} sub={`${totals.outOfArea} leads`} subTone={oopPct > 15 ? "warn" : undefined} />
        <Stat label="Unmapped" value={String(totals.unmapped)} sub="need attribution" subTone={totals.unmapped > 0 ? "warn" : undefined} />
      </dl>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search sources…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="h-8 pl-8 text-[13px]"
          />
        </div>
        <div role="tablist" className="inline-flex rounded-md border border-border bg-card p-0.5 flex-wrap">
          <FilterChip label="All channels" active={channelFilter === "all"} onClick={() => setChannelFilter("all")} />
          {GROWTH_CHANNELS.filter(c => c.type !== "coming_soon").map(c => (
            <FilterChip key={c.id} label={c.name} active={channelFilter === c.id} onClick={() => setChannelFilter(c.id)} />
          ))}
          <FilterChip label="Unattributed" active={channelFilter === "unattributed"} onClick={() => setChannelFilter("unattributed")} />
        </div>
      </div>

      {/* Table */}
      <div>
        <div className="grid grid-cols-[minmax(0,1.6fr)_120px_70px_70px_80px_80px_90px] items-center gap-3 px-4 py-2.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border">
          <span>Source</span>
          <span>Channel</span>
          <span className="text-right">Leads</span>
          <span className="text-right">Net</span>
          <span className="text-right">Quality</span>
          <span className="text-right">Dupes</span>
          <span className="text-right font-semibold text-foreground/80">Net conv</span>
        </div>
        <ul className="divide-y divide-border border-b border-border">
          {filtered.length === 0 && (
            <li className="px-4 py-8 text-center text-[13px] text-muted-foreground">No sources match the current filter.</li>
          )}
          {filtered.map(r => (
            <SourceRowEl key={r.name} row={r} />
          ))}
        </ul>
      </div>

      {/* Footer link */}
      <div className="flex items-center justify-between pt-2 text-[12px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5 text-success" />
          Same dataset as the Channels lens — rolls up identically.
        </span>
        <Link to="/lead-integrity" className="text-primary hover:underline inline-flex items-center gap-1">
          Open Lead Integrity <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}

function SourceRowEl({ row }: { row: SourceRow }) {
  const dupeWarn = row.dupePct > 15;
  const qualityWarn = row.intakeTotal > 0 && row.validPct < 60;
  const noChannel = !row.channelId;

  return (
    <li
      className="grid grid-cols-[minmax(0,1.6fr)_120px_70px_70px_80px_80px_90px] items-center gap-3 px-4 py-3 text-[13px] hover:bg-muted/30 transition-colors"
      data-status={noChannel ? "warn" : undefined}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-foreground truncate">{row.name}</span>
          {noChannel && (
            <span className="inline-flex items-center gap-1 text-[10px] text-warning">
              <AlertTriangle className="h-3 w-3" /> unmapped
            </span>
          )}
        </div>
        {row.intakeTotal > 0 && (
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {row.intakeApproved}/{row.intakeTotal} intake approved
            {row.outOfArea > 0 && ` · ${row.outOfArea} out-of-area`}
          </p>
        )}
      </div>

      <span className={cn("text-[12px] truncate", noChannel ? "text-warning" : "text-muted-foreground")}>
        {row.channelName}
      </span>
      <span className="text-right tabular-nums text-foreground">{row.leads || "—"}</span>
      <span className="text-right tabular-nums text-foreground">{row.wonNet || "—"}</span>
      <span className={cn(
        "text-right tabular-nums",
        qualityWarn ? "text-warning font-semibold" : "text-foreground",
      )}>
        {row.intakeTotal > 0 ? fmtPct(row.validPct) : <span className="text-muted-foreground/40">—</span>}
      </span>
      <span className={cn(
        "text-right tabular-nums",
        dupeWarn ? "text-warning font-semibold" : "text-muted-foreground",
      )}>
        {row.intakeTotal > 0 ? fmtPct(row.dupePct) : <span className="text-muted-foreground/40">—</span>}
      </span>
      <span className={cn(
        "text-right tabular-nums font-semibold",
        row.leads === 0 ? "text-muted-foreground/40" :
        row.netConvPct >= 25 ? "text-success" :
        row.netConvPct < 10 ? "text-destructive" :
        "text-foreground",
      )}>
        {row.leads > 0 ? fmtPct(row.netConvPct) : "—"}
      </span>
    </li>
  );
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-2.5 py-1 text-[11px] font-medium rounded-[6px] transition-colors",
        active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
    </button>
  );
}

function Stat({ label, value, sub, subTone, emphasis }: { label: string; value: string; sub?: string; subTone?: "warn"; emphasis?: boolean }) {
  return (
    <div className="flex flex-col">
      <dt className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground">{label}</dt>
      <dd className={cn("tabular-nums tracking-tight mt-1", emphasis ? "text-[20px] font-semibold text-foreground" : "text-[18px] font-semibold text-foreground")}>{value}</dd>
      {sub && <span className={cn("text-[10px] mt-0.5", subTone === "warn" ? "text-warning" : "text-muted-foreground")}>{sub}</span>}
    </div>
  );
}
